import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_PACKAGE_MODULES_FORM, WEB_COMPONENT_SUSPEND_PROTECTION } from '../../../../../webcomponents.constants';
import { html, LitElement, PropertyValues } from 'lit';
import { styles } from './view/styles';
import { InputWidthValues } from '../../../../../ui/gzInput';
import { translateFn } from '../../../../../utils/translator.helper';
import { SelectOption } from '../../../../../ui/gzDropdown/types';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { GzDropdownWebComponent } from '../../../../../ui/gzDropdown';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { Subscription } from 'rxjs';
import { NetworkPopUpFooterContentWebComponent } from '../../NetworkPopUpFooterContentWebComponent';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { GzTextWebComponent } from '../../../../../ui/gzText';
import { PackageModulesFormWebComponent } from '../../../../../packages/common/packageModulesForm';
import { PackageActionContext } from '../../../../../packages/common/types/PackageActionContext';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { ModuleOptions } from './types';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import _ from 'lodash';

@customElement(WEB_COMPONENT_SUSPEND_PROTECTION)
export class GzSuspendProtectionWebComponent extends LitElement {
  @query('gz-form-field#suspend-protection-task-name')
  taskNameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('gz-form-field#suspend-protection-modules-form')
  modulesFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('gz-form-field#suspend-protection-time-form')
  timeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('gz-form-field#suspend-protection-agree-form')
  agreeFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-form-field#suspend-protection-run-form')
  runFullScanFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#protection-disabled-text')
  protectionDisabledText: GzTextWebComponent;

  @query('#warn-protected')
  warnProtectedText: GzTextWebComponent;

  @query('#receive-notifications-text')
  receiveNotificationsText: GzTextWebComponent;

  @query('#warn-malware')
  warnMalwareText: GzTextWebComponent;

  @query(WEB_COMPONENT_PACKAGE_MODULES_FORM)
  modulesForm: PackageModulesFormWebComponent;

  private formGroup: FormGroup;
  private formGroupSubscription: Subscription | null;

  public dependencies: any;
  public availableFeatures: any;
  public popUpFooterContent: NetworkPopUpFooterContentWebComponent;
  public endpointName = "";
  public timeOptions: Array<SelectOption> = [
    {
      title: translateFn('{MINUTES} minutes', {MINUTES: 15}),
      value: 15,
    },
    {
      title: translateFn('{MINUTES} minutes', {MINUTES: 30}),
      value: 30,
    },
    {
      title: translateFn('{HOURS} hour', {HOURS: 1}),
      value: 60,
    },
    {
      title: translateFn('{HOURS} hours', {HOURS: 2}),
      value: 2*60,
    },
    {
      title: translateFn('{HOURS} hours', {HOURS: 4}),
      value: 4*60,
    },
  ];
  public listOfDisabledModules: Array<string> = ["deviceControl", "powerUser", "encryption", "patchManagement"];

  static get styles() {
    return styles;
  }

  public unsubscribeFromObservables(): void {
    this.formGroupSubscription?.unsubscribe();
  }

  disconnectedCallback() {
    this.unsubscribeFromObservables();
    super.disconnectedCallback();
  }

  render() {
    return html`
      <div class="container pt-4 pl-5 pb-4">
        <gz-title id="suspend-protection-general" fontsize="18" fontweight="500" class="form-subtitle pb-2">${translateFn('General')}</gz-title>

        <gz-form-field id='suspend-protection-target' class='label-mw-250' label='${translateFn('Selected endpoint')}' readonly>
          <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}" value="${this.endpointName}"></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id='suspend-protection-task-name' class='label-mw-250' label='${translateFn('Task name')}'>
          <gz-input gz-form-input inputWidth="${InputWidthValues.LARGE}"></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-title id="suspend-protection-settings" fontsize="18" fontweight="500" class="form-subtitle pt-4 pb-2">${translateFn('Settings')}</gz-title>

        <gz-text fontWeight="400" fontSize="14" lineHeight="1.2" id="protection-disabled-text">${translateFn('The protection will be disabled immediately for the selected targets. Specify the modules and the period of inactivity below.')}</gz-text>

        <gz-form-field id='suspend-protection-modules-form' class='label-mw-250 pt-2' label='${translateFn('Modules')}' style="width:500px;">
          <gz-radio-group id='suspend-protection-modules-options-group' class="pt-1" gz-form-input @radioSelectionChanged="${this.handleModuleChanged}" selectedoption="1">
            <gz-radio id='suspend-protection-modules-all' class='mb-3' label='${translateFn('All supported modules')}' value="${ModuleOptions.ALL}"></gz-radio>
            <gz-radio id='suspend-protection-modules-specific' class='mb-2' label='${translateFn('Select modules')}' value="${ModuleOptions.SPECIFIC}"></gz-radio>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-package-modules-form class='pb-2' data-display-only-modules @onReconfigureAgentModulesChanged="${this.handleReconfigureModulesChanged}"></gz-package-modules-form>

        <gz-form-field id='suspend-protection-time-form' class='label-mw-250' label='${translateFn('Resume protection after')}'>
          <gz-dropdown gz-form-input id="suspend-protection-time-dropdown" options="${JSON.stringify(this.timeOptions)}"></gz-dropdown>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-info-box style="padding-left:0" id='warn-protected' class='box-info pb-4' severity='warning'>${translateFn('The selected endpoint will be unprotected for the specified time interval.')}</gz-info-box>

        <gz-form-field id="suspend-protection-agree-form" class="field-container">
          <gz-checkbox gz-form-input label="${translateFn('I understand the risks of disabling the protection effective immediately for the selected endpoint.')}"></gz-checkbox>
        </gz-form-field>

        <gz-text class="pb-4" fontWeight="400" fontSize="14" lineHeight="1.2" id="receive-notifications-text"></gz-text>
        <gz-text class="pb-2" fontWeight="400" fontSize="14" lineHeight="1.2" id="recommend-text">${translateFn('We recommend starting a full malware scan on the unprotected endpoint immediately after the selected time interval ends.')}</gz-text>

        <gz-form-field id="suspend-protection-run-form" class="field-container">
          <gz-checkbox gz-form-input id="suspend-protection-run-checkbox" @change="${this.handleRunEndpointFullScanChanged}" label="${translateFn('Start a full malware scan after enabling the protection on the selected endpoint.')}"></gz-checkbox>
        </gz-form-field>

        <gz-text class="pb-1 helper-text" fontWeight="400" fontSize="12" lineHeight="1.2" id="full-scan-task-text">${translateFn('A full malware scan task will be performed at the end of the specified time interval.')}</gz-text>
        <gz-info-box style="padding-left:0" id='warn-malware' class='box-info pb-1' severity='warning'>${translateFn('To avoid malware threats, make sure to scan the selected endpoint.')}</gz-info-box>
      </div>
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildFormGroup();
    this.setTranslatedStrings();
    this.subscribeToFormFields();
    this.setFormValidators();
  }

  public configureModuleForm() {
    this.modulesForm.formContext = PackageActionContext.Edit;
    this.modulesForm.useDefaultValues = false;
    this.modulesForm.setAvailableFeatures(this.availableFeatures);
    this.modulesForm.injectDependencies(this.dependencies);
    this.modulesForm.start().then(this.initModulesSection.bind(this)).catch(console.error);
  }

  private initModulesSection(): void {
    const modulesForm: PackageModulesFormWebComponent =  this.modulesForm;
    for (const moduleField of Object.values(modulesForm.controller.view.modulesFields)) {
      moduleField.$uiCheckbox.Validators = [CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm))];
    }
    modulesForm.controller.view.dom.$mailServersCheckbox.Validators = [CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm))];
    modulesForm.controller.view.dom.$updateServerCheckbox.Validators = [CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm))];
    modulesForm.controller.view.dom.$patchManagementServerCheckbox.Validators = [CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm))];

    modulesForm.controller.view.formGroup.setDisabled(false);
    modulesForm.packageData = {
      productType: this.modulesForm.getDefaultOperationMode,
      modules: this.availableFeatures,
      removeCompetitors: true,
    };

    this.modulesForm.setReadonlyModules(false, ["antimalware"]);
    this.modulesForm.setReadonlyModules(true, this.listOfDisabledModules);
  }

  private buildFormGroup() {
    this.formGroup = FormGroup.build({
      taskName: this.taskNameFormField,
      modulesOption: this.modulesFormField,
      duration : this.timeFormField,
      understandTheRisk: this.agreeFormField,
      runScanOnResume: this.runFullScanFormField,
    });

    this.formGroup.value = {
      taskName: translateFn("Suspend endpoint protection {DATE}", {DATE: new Date().toISOString().split('T')[0]}),
      modulesOption: ModuleOptions.ALL,
      duration : 15,
      understandTheRisk: false,
      runScanOnResume: false,
    }
  }

  private setFormValidators() {
    if (this.taskNameFormField) {
      this.taskNameFormField.Validators = [RequiredValidator(translateFn('Required field'))];
    }
    if (this.agreeFormField) {
      this.agreeFormField.Validators = [CustomizableValidator((v) => ({valid: v}))];
    }
  }

  private subscribeToFormFields() {
    this.formGroupSubscription = this.formGroup.valid$.subscribe(valid => {
        const btnSave = this.popUpFooterContent?.shadowRoot?.querySelector("#confirmButton") as GzButtonWebComponent;
      if (btnSave) {
        btnSave.disabled = !valid;
      }
      if (this.modulesForm.controller) {
        this.modulesForm.checkIfAtLeastOnModuleIsChecked();
      }
    });
  }

  private setTranslatedStrings(): void {
    this.receiveNotificationsText.innerHTML = translateFn(
      'Activate the {OPEN_TAG} Endpoint module status {CLOSE_TAG} notification to receive alerts when the protection is enabled.',
      { OPEN_TAG: '<b>', CLOSE_TAG: '</b>'}
    );
  }

  public getFormValue(): any {
    const moduleValues = _.clone(this.modulesForm?.value?.settings?.modules);
    if (moduleValues && this.modulesFormField.value === ModuleOptions.ALL) {
      Object.keys(moduleValues).forEach((moduleKey) => {
        moduleValues[moduleKey] = true;
      });
    }

    for (const module of this.listOfDisabledModules) {
      if (_.isObject(moduleValues) && moduleValues.hasOwnProperty(module)) {
        delete moduleValues[module];
      }
    }

    return _.merge(this.formGroup.value, {modules: moduleValues});
  }

  public handleModuleChanged(event: CustomEvent) {
    toggleVisibility(this.modulesForm, event.detail.selectedValue === ModuleOptions.SPECIFIC);
    if (this.modulesForm.controller) {
      this.modulesForm.checkIfAtLeastOnModuleIsChecked();
    }
  }

  public handleReconfigureModulesChanged(event: CustomEvent) {
    const btnSave = this.popUpFooterContent?.shadowRoot?.querySelector("#confirmButton") as GzButtonWebComponent;
    if (btnSave) {
      btnSave.disabled = (event.detail.allModulesUnchecked && this.modulesFormField.value === ModuleOptions.SPECIFIC) || !this.formGroup.valid;
    }
  }

  public handleRunEndpointFullScanChanged(event: CustomEvent) {
     toggleVisibility(this.warnMalwareText, !event.detail.checked);
  }
}
